import { Injectable } from "@angular/core";
import { User, UserI } from "src/app/shared/models/user";
import {
  ROUTE_APP_TOKEN,
  ROUTE_USER_LOGIN,
  ROUTE_USER_ROLES,
} from "src/app/shared/constants/route-paths";
import { Observable } from "rxjs/internal/Observable";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import {
  ACCESS_TOKEN_KEY,
  CookieService,
  REFRESH_TOKEN_KEY,
  SessionStorageService,
} from "@collab/comp-library";
import { BackendService } from "src/app/shared/services/backend.services";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  public currentUser: Observable<User> | undefined;
  public userDetails!: UserI;
  public accessToken: any;
  public appToken: string | null = null;
  private apiServiceUrl = environment.apiUrl;
  private appAccessTokenSubject = new BehaviorSubject<string | null>(null);
  public appAccessToken$ = this.appAccessTokenSubject.asObservable();
  private tokensSubject$ = new BehaviorSubject<any>(null);
  public tokens$ = this.tokensSubject$.asObservable();
  private newPasswordSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private localStorage: SessionStorageService,
    private backendService: BackendService
  ) { }

  /**
   *
   * this method will get the current user value
   * @readonly
   * @param {userName} string
   * @param {password} string
   * @type {User}
   * @memberof AuthenticationService
   */
  public logIn(userName: string, password: string, appToken: any): any {
    let requestParams: any;
    const tokenValue = appToken.token;
    requestParams = {
      userName: encodeURI(userName),
      password: encodeURI(password)
    };

    const headers = {
      headers: new HttpHeaders()
        .set("Content-Type", "application/json")
        .set("X-Ava-App-Access-Token", tokenValue),
      observe: "response" as "body",
    };
    const url = this.apiServiceUrl + ROUTE_USER_LOGIN;

    return this.http.post(url, requestParams, headers);
  }

  /**
   *
   * this method will get the app token
   * @readonly
   * @memberof AuthenticationService
   */
  public getAppToken(): Observable<any> {
    const requestParams = {
      clientId: "JXCY0X2C5UJ2QAR2M389AA",
      clientSecret: "RvIIz5ECA18EkEgMVkHRfZ",
      scope: "app",
      grantType: "client_credentials",
    };
    const headers = {
      headers: new HttpHeaders().set("Content-Type", "application/json"),
    };
    const url = this.apiServiceUrl + ROUTE_APP_TOKEN;

    return this.http.post(url, requestParams, headers);
  }

  public getAmlAppToken(): Observable<any> {

    const headers = {
      headers: new HttpHeaders().set("Content-Type", "application/json"),
    };
    const url = this.apiServiceUrl + ROUTE_APP_TOKEN;

    return this.http.post(url, headers);
  }
  /**
   * this method will save the user details from the login authentication.
   * @param {userDetails}
   * @memberof AuthenticationService
   */
  public setUserDetails(userDetails: any): void {
    this.userDetails = userDetails?.body;
  }

  /**
   * this method will return the saved userdetails
   *
   * @return {*}  {UserI}
   * @memberof AuthenticationService
   */
  public getUserDetails(): UserI {
    return this.userDetails;
  }

  /**
   * this methid will logOut the user from the application
   *
   * @memberof AuthenticationService
   */
  public logOut(isIdleTimeOut: any) {
    const headers = {
      headers: new HttpHeaders().set("Content-Type", "application/json"),
    };
    const url = this.apiServiceUrl + "/user/signout";

    return this.http.post(url, headers).subscribe(() => {
      if (this.cookieService.getCookie(ACCESS_TOKEN_KEY)) {
        this.cookieService.setCookie(ACCESS_TOKEN_KEY, "", 0);
      } 

      if (this.cookieService.getCookie(REFRESH_TOKEN_KEY)) {
        this.cookieService.setCookie(REFRESH_TOKEN_KEY, "", 0);
      } 

      this.localStorage.setStorage("userId", "");

      if (isIdleTimeOut) {
        window.location.href =
          environment.uiUrl + "/#/login?error=invalid_token";
      } else {
        window.location.href = environment.uiUrl + "/#/login";
      }
    });
  }

  public passwordReset(formValues: any) {
    const headers = {
      headers: new HttpHeaders().set("Content-Type", "application/json"),
    };
    const url = this.apiServiceUrl + "/user/password/change";
    let requestParams = {
      userName: this.userDetails?.user_name,
      oldPassword: formValues.oldPassword,
      newPassword: formValues.newPassword,
    };

    return this.http.post(url, requestParams, headers);
  }

  public getUserRoles(): any {
    const tokenDetails = JSON.parse(atob(this.accessToken.split(".")[1]));
      
    const rolesRequest = {
      tenant: "AVA_IAM_Default",
      email: tokenDetails.email,
      firstName: tokenDetails.given_name,
      lastName: tokenDetails.family_name,
      userName: tokenDetails.email,
      roles: tokenDetails.realm_access.roles,
    };

    return this.backendService.post(ROUTE_USER_ROLES, rolesRequest);
  }

  public passwordResetEmail(email: string, appToken: any): Observable<any> {
    const requestParams = {
      email: email,
    };
    const tokenValue = appToken.token;
    const headers = {
      headers: new HttpHeaders()
        .set("Content-Type", "application/json")
        .set("X-Ava-App-Access-Token", tokenValue),
      observe: "response" as "body",
    };
    const url = `${this.apiServiceUrl}/user/password/reset`;
    return this.http.post(url, requestParams, headers);
  }

  public setNewPasswordState(value: boolean): void {
    this.newPasswordSubject.next(value);
  }

  public getNewPasswordState(): Observable<boolean> {
    return this.newPasswordSubject.asObservable();
  }

}